import { cn } from '@/lib/utils';

/** FNV-1a de 32 bits: nomes parecidos caem em desenhos bem diferentes. */
function hash(texto: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Grade 5×5 espelhada na vertical (15 módulos sorteados, como um identicon),
 * sempre a mesma para o mesmo nome. Refaz o sorteio quando o desenho sai
 * vazio ou cheio demais para ser reconhecível.
 */
export function pixelAvatarCells(semente: string): boolean[] {
  const base = semente.trim().toLowerCase() || '?';
  let bits = hash(base);
  for (let tentativa = 1; tentativa < 8; tentativa += 1) {
    let acesos = 0;
    for (let i = 0; i < 15; i += 1) acesos += (bits >>> i) & 1;
    if (acesos >= 5 && acesos <= 11) break;
    bits = hash(`${base}#${tentativa}`);
  }
  return Array.from({ length: 25 }, (_, k) => {
    const coluna = k % 5;
    const linha = (k / 5) | 0;
    const espelhada = coluna > 2 ? 4 - coluna : coluna;
    return ((bits >>> (linha * 3 + espelhada)) & 1) === 1;
  });
}

/**
 * Avatar de quem não tem foto: módulos no verde da marca sobre o grafite, na
 * mesma linguagem do símbolo do DevClub. Decorativo: o nome da pessoa já
 * aparece ao lado ou no rótulo de quem usa o avatar.
 */
export function PixelAvatar({
  seed,
  className,
}: {
  /** O nome da pessoa: o mesmo nome dá o mesmo desenho em toda a plataforma. */
  seed: string;
  className?: string;
}) {
  const desenho = pixelAvatarCells(seed)
    .flatMap((aceso, k) =>
      aceso ? [`M${k % 5} ${(k / 5) | 0}h1v1h-1z`] : [],
    )
    .join('');
  return (
    <svg
      viewBox="-1.5 -1.5 8 8"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
      data-avatar-gerado=""
      className={cn('block h-full w-full', className)}
    >
      <rect x={-1.5} y={-1.5} width={8} height={8} fill="hsl(var(--secondary))" />
      <path d={desenho} fill="hsl(var(--primary))" />
    </svg>
  );
}
